import React, { useRef } from "react";
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import withMainContainer from "../components/WithMainContainer";
import { ProductPurchaseForm } from "../components/ProductPurchaseForm.js";
import { useScrollTo } from "../util/react_hooks.js";
import SCButton from "../components/SCButton.js";
import Banner from "../components/Banner.js";
import { imagekitStaticURL } from "../util/util.js";
import Image from "../components/Image.js";
import StackWithArrow from "../components/StackWithArrow.js";
import { openModal } from "../components/Modals.js";

const learningItems = [
  "מהו טיהור אנרגטי ולמה הוא חשוב",
  // "ההבדל בין ניקיון פיזי לניקיון אנרגטי",
  "איך לזהות מתי הבית או המרחב זקוקים לניקוי",
  "עבודה עם צמחים, שרפים וחומרי טיהור",
  "מתי להשתמש במרווה, פאלו סנטו וחומרים נוספים",
  "החשיבות של העלאת תדר והכנסת ברכה לאחר טיהור",
  "מתכונים מיוחדים לסירי טיהור וברכה שאפשר להכין בבית",
  "טכניקות פשוטות לניקוי עצמי והסרת עומס אנרגטי",
  "עבודה עם כוונה ככלי מרכזי בכל תהליך אנרגטי",
  "כלים ליצירת מרחב שתומך בך ובבני הבית",
];

const bonusItems = [
  "צמחים לטיהור",
  "שרפים וחומרי קטורת",
  "חומרים להכנסת ברכה",
  "הסברים על מקורות ושימושים",
  "הנחיות עבודה מעשיות",
  "התאמת חומרים למצבים שונים",
];

const fitItems = [
  "למי שמרגישה שהמרחב משפיע עליה.",
  "למי שסקרנית לגבי העולם האנרגטי ורוצה ללמוד אותו בצורה נגישה ומעשית.",
  "למי שרוצה להבין איך לעבוד נכון עם טיהור, הגנה וברכה.",
  "למי שמרגישה דברים שלא תמיד יש לה מילים להסביר.",
  "למי שמחפשת כלים פשוטים שיכולים להשתלב בחיים האמיתיים.",
  "ולכל מי שרוצה ליצור בית שמרגיש כמו מקום שמטעין אותה - ולא כמו עוד מקום שלוקח ממנה אנרגיה.",
];

const openPayformModal = ()=>openModal({
    content: <ProductPurchaseForm
      productId="cleansingWorkshopJune2026"
      btnText="מעבר לתשלום מאובטח"
      noOutline
      //maxWidth="250px"
    />,
});

const EnergyCleansingLandingPage = () => {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"));
  const purchaseFormRef = useRef();
  const scrollToPurchaseSection = useScrollTo(purchaseFormRef);

  return (
    <Box dir="rtl" sx={{ overflow: "hidden", bgcolor: "#FFF7EE" }}>
      <Stack direction="column">

        <Banner
          image={imagekitStaticURL`header-header.png`}
          placeholderColor="#242328"
          backgroundPosition="bottom"
          padding={0}
          width="100%"
        >
          <Box sx={heroOverlay}>
            <Container maxWidth="md">
              <Stack spacing={3} alignItems="center" textAlign="center">
                <Typography variant="h2" sx={heroTitle}>
                  הבית שלך נקי
                  <br />
                  אז למה הוא עדיין מרגיש כבד?
                </Typography>

                <Typography variant="h5" sx={heroSubtitle}>
                  סדנת זום חווייתית לשחרור מה שכבר לא משרת אותך, יצירת התחלה חדשה והכנסת יותר קלילות, הרמוניה וברכה לחיים.
                </Typography>

                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.2}>
                  {[
                    "📅 02.07.26 18:30",
                    // "⏰ כשעתיים וחצי",
                    "💻 בזום",
                    "🎥 כולל הקלטה",
                    "📖 מדריך במתנה",
                  ].map((item) => (
                    <Chip key={item} label={item} sx={heroChip} />
                  ))}
                </Stack>

                <SCButton onClick={scrollToPurchaseSection} variant="contained" sx={ctaButton}>
                  אני רוצה לתפוס מקום בסדנה
                </SCButton>
              </Stack>
            </Container>
          </Box>
        </Banner>

        <Section>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.2}>
                <Title>כולנו למדנו איך לנקות את הבית</Title>

                <Text>
                  אנחנו יודעים לשטוף רצפה, לנקות אבק, לפתוח חלונות, ולסדר את
                  הדברים במקום.
                </Text>

                <Callout>
                  אבל אף אחד לא לימד אותנו איך לתחזק אנרגטית את עצמנו ואת הבית שלנו.
                </Callout>

                <Text>
                  ובכל זאת... רובנו מרגישים בזה.
                  <br />
                  אנחנו נכנסים למקום ומיד יודעים אם נעים לנו בו או לא.
                </Text>

                <Text>
                  יש בתים שמרגישים עוטפים, רגועים ונעימים.
                  <br />
                  אבל לפעמים גם כשמקומות נקיים ומסודרים - משהו בהם מרגיש כבד,
                  עייף ותקוע.
                </Text>

                <Text>
                  ואותו דבר גם לגבינו... אנחנו סופגים אנרגיה מאנשים ומקומות.
                  סופגים מתח, לחץ ופחדים.
                </Text>

                <Text>
                  ולפעמים אנחנו אפילו לא שמים לב כמה דברים אנחנו נושאים איתנו.
                </Text>

                <Text sx={{fontWeight: 'bold'}}>
                  כמו שאבק מצטבר בבית,
                  <br />
                  כך גם מצטברות אנרגיות שחשוב לדעת לשחרר.
                </Text>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <ImageFrame>
                <Image
                  imagekit_src="cleansing_broom.png"
                  sx={{ width: "100%", maxHeight: 360, objectFit: "contain" }}
                />
              </ImageFrame>
            </Grid>
          </Grid>
        </Section>

        <Section soft>
          <Stack spacing={2.2} alignItems="center" textAlign="center">
            <Title>אולי את מכירה את זה...</Title>
            <Text center>את מסדרת ומנקה, ועדיין משהו בבית לא מרגיש לך נכון.</Text>
            <Text center>אולי את מרגישה עייפות שלא באמת קשורה לשינה.</Text>
            <Text center>אולי לוקח לך זמן להתאושש אחרי מפגש עם אנשים מסוימים.</Text>
            <Text center>אולי יש תקופות שבהן הכול מרגיש כבד יותר.</Text>
            <Text center>אולי את מרגישה דברים שאין לך תמיד דרך להסביר.</Text>
            <Text center>
              ואולי כבר קרה שסיפרת למישהו על תחושה, חלום או ידיעה פנימית...
              <br />
              וקיבלת מבט של: <em>"על מה את מדברת?"</em>
            </Text>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 2 }}>
              אם את מכירה את ההרגשה הזאת, הסדנה הזאת נוצרה בדיוק בשבילך!
            </Typography>
          </Stack>
        </Section>

        <Section>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5} order={{ xs: 2, md: 1 }}>
              <ImageFrame>
                <Image
                  imagekit_src="cleansing_materials.png"
                  sx={{ width: "100%", maxHeight: 360, objectFit: "contain" }}
                />
              </ImageFrame>
            </Grid>

            <Grid item xs={12} md={7} order={{ xs: 1, md: 2 }}>
              <Stack spacing={2.2}>
                <Title>טיהור אנרגטי הוא לא רק לאנשים רוחניים</Title>
                <Text>בשנים האחרונות יותר ויותר אנשים משתמשים במרווה, פאלו סנטו וקטורות.</Text>
                <Text>אבל הנה האמת שאף אחד כמעט לא מדבר עליה:</Text>
                <Callout>מרווה היא לא פתרון לכל דבר.</Callout>
                <Text>
                  בדיוק כמו שלא כל כתם מנקים עם אותו חומר,
                  <br />
                  גם לא בכל מצב אנרגטי מטפלים באותה דרך.
                </Text>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.7 }}>
                  כדי לעבוד נכון עם אנרגיה,
                  <br />
                  צריך להבין מה עושים,
                  <br />
                  מתי עושים,
                  <br />
                  ולמה.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Section>

        <Box sx={{ bgcolor: "#d4b0b555", py: { xs: 7, md: 9 }, px: 2 }}>
          <Container maxWidth="md">
            <Stack spacing={2.2} alignItems="center" textAlign="center">
              <Title>ומה אם היית יודעת איך להשפיע על האנרגיה סביבך?</Title>
              <Text center>לא רק לספוג אותה, לא רק להתמודד איתה.</Text>
              <Text center>אלא ממש לעבוד איתה בצורה מודעת.</Text>
              <Text center>לדעת איך לטהר את עצמך ואת הבית.</Text>
              <Text center>לדעת איך ליצור מרחב שמטעין אותך במקום לרוקן אותך.</Text>
              <Text center>לדעת איך להכניס יותר שקט, יותר קלילות, יותר הרמוניה ויותר ברכה.</Text>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                זה בדיוק מה שנלמד בסדנה.
              </Typography>
            </Stack>
          </Container>
        </Box>

        <Section wide>
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Title>מה נלמד בסדנה?</Title>
            <Grid container spacing={2.5} sx={{transform: 'translate(-10px)'}}>
              {learningItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <InfoCard icon="🌿">{item}</InfoCard>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Section>

        <Banner image={imagekitStaticURL`header-private-lessons.png`} placeholderColor="#242328">
          <Box sx={darkSectionOverlay}>
            <Container maxWidth="md">
              <Stack spacing={2.3} alignItems="center" textAlign="center">
                <Typography variant="h3" sx={darkTitle}>
                  החלק הכי מיוחד בסדנה
                </Typography>
                <Typography variant="h4" color="primary.light" sx={{ fontWeight: 700, mb: 1 }}>
                  טיהור עצמי בלייב בשיטת הלימפיה
                </Typography>
                <Text light center>
                  במהלך הסדנה נעשה יחד טיהור עצמי באמצעות ביצה, בהשראת מסורת הלימפיה מדרום אמריקה.
                </Text>
                <Text light center>
                  זוהי שיטה מסורתית המשמשת לניקוי אנרגטי, הסרת כובד, עין הרע ואנרגיות שאינן משרתות אותנו.
                </Text>
                <Text light center>כל משתתפת תבצע את התהליך בעצמה בזמן אמת.</Text>
                <Text light center>לאחר מכן אלמד אותך כיצד לקרוא ולהבין את מה שהתנקה בתהליך.</Text>
                <Typography sx={darkBold}>
                  זה הרגע שבו כל מה שלמדנו מפסיק להיות רק רעיון או ידע,
                  והופך לחוויה אישית, מוחשית ומרגשת.
                </Typography>

                <Box sx={smallNote}>
                  * לצורך הלימפיה נשתמש בציוד ביתי פשוט וזמין.
                  <br />
                  הביצה אינה נשברת על הגוף אלא לתוך כלי עם מים כחלק מתהליך הטיהור.
                  <br />
                  לפני הסדנה תישלח לנרשמות רשימת מצרכים קצרה ומסודרת.
                </Box>
              </Stack>
            </Container>
          </Box>
        </Banner>

        <Section>
          <Stack spacing={2.2} alignItems="center" textAlign="center">
            <Title>אבל טיהור הוא רק חצי מהדרך</Title>
            <Callout>זאת אחת הטעויות הכי נפוצות(!)</Callout>
            <Text center>אנשים עושים ניקוי, ומבחינתם זה הסוף.</Text>
            <Text center>אבל כל ניקוי יוצר חלל, והחלל הזה מבקש להתמלא.</Text>
            <Text center sx={{fontWeight: 'bold'}}>לכן בסדנה נלמד גם איך להכניס למרחב אנרגיה רצויה.</Text>
            <Text center>איך לעבוד עם ברכות, עם כוונה, עם תפילה,</Text>
            <Text center>
              ועם כלים פשוטים שיכולים לעזור ליצור בבית תחושה של הרמוניה, שמחה,
              אהבה וחיבור,
              <br />
              וכך להשפיע לטובה על כל האנשים שחיים בבית.
            </Text>
          </Stack>
        </Section>

        <Section soft>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={1.7}>
                <Title>כל משתתפת תקבל במתנה את</Title>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                  מדריך החומרים השלם לטיהור וברכה
                </Typography>
                <Text>ספרון דיגיטלי מקיף בן 26 עמודים.</Text>
                <Text>מדריך מעשי שילווה אותך גם הרבה אחרי שהסדנה תסתיים.</Text>
                <Text>בתוכו תמצאי:</Text>
                <Stack direction="column" gap={1.5}>
                  {bonusItems.map(item=><Typography sx={bonusItem}>✨ {item}</Typography>)}
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Image
                imagekit_src="cleansing_book_cover_2.png"
                alt="מדריך החומרים השלם לטיהור וברכה"
                sx={{
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 3,
                  boxShadow: "0 28px 70px rgba(58,42,34,0.22)",
                  transform: { xs: "none", md: "rotate(2deg)" },
                }}
              />
            </Grid>
          </Grid>
        </Section>

        <Section>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Title>למי הסדנה מתאימה?</Title>
            <Grid container spacing={2.2} sx={{transform: 'translate(-10px)'}}>
              {fitItems.map((item) => (
                <Grid item xs={12} sm={6} key={item}>
                  <InfoCard icon="🌙">{item}</InfoCard>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Section>

        <StackWithArrow backgroundColor="#f1e0db" arrowIndex={1}>
          <Stack spacing={2.2} alignItems="center" textAlign="center" zIndex={2}
            marginTop={7} marginBottom={7} paddingLeft={3} paddingRight={3}>
            <Title>למה דווקא עכשיו?</Title>
            <Text center>זו הפעם הראשונה שאני פותחת את התוכן הזה כסדנת זום קבוצתית.</Text>
            <Text center>
              עד היום לימדתי את רוב הכלים האלו במפגשים אישיים, תהליכים פרטיים
              ושיעורים מכושפים אחד על אחד.
            </Text>
            <Text center>
              החלטתי לפתוח את הסדנה הזו כי אני מאמינה שהידע הזה צריך להיות נגיש
              ליותר אנשים.
            </Text>
            <Text center>לא רק למי שכבר עובדים איתי באופן אישי.</Text>
            <Text center>ולא רק למי שמרגישים שהם "רוחניים מספיק".</Text>
            <Text center>
              אלא לכל מי שמרגישה שהמרחב משפיע עליה ורוצה ללמוד לעבוד איתו בצורה
              פשוטה, בטוחה ומעשית.
            </Text>
          </Stack>
        </StackWithArrow>

        <PriceSection ref={purchaseFormRef}/>

        <Section>
          <Stack spacing={2.2} alignItems="center" textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              לפעמים לא צריך לשנות את כל החיים.
            </Typography>
            <Text center>לפעמים מספיק לשחרר את מה שכבר לא שייך אליהם.</Text>
            <Text center>
              לפנות מקום. לנשום עמוק יותר
              <br />
              <strong>ולהיזכר שיש לך הרבה יותר השפעה על האנרגיה שלך ממה שאולי חשבת.</strong>
            </Text>
            <Text center>כי הבית שבו אנחנו חיים משפיע עלינו כל יום.</Text>
            <Text center>
              ואם כבר יש לך את היכולת להשפיע עליו בחזרה - למה לא ללמוד איך לעשות את זה נכון?
            </Text>

            <Typography sx={{ ...textStyle, mt: 3 }}>
              🌿 אם הרגשת פעם שהמרחב משפיע עלייך יותר ממה שאנשים סביבך מבינים,
              <br />
              אם רצית ללמוד איך לטהר, לברך וליצור בית שתומך בך במקום לרוקן אותך -
              <br />
              אני מזמינה אותך להצטרף אליי.
            </Typography>

            <Typography color="primary" sx={{ fontSize: 24, mt: 2, mb: 3 }}>
              מחכה לפגוש אותך בסדנה,
              <br />
              <strong>סימונה צ'רי</strong>
            </Typography>

            {/* <ProductPurchaseForm
              productId="cleansingWorkshopJune2026"
              btnText="אני מצטרפת לסדנה"
              ref={purchaseFormRef}
            /> */}
          </Stack>
        </Section>
      </Stack>
    </Box>
  );
};

function Section({ children, soft = false, wide = false }) {
  return (
    <Box sx={{ py: { xs: 6, md: 9 }, px: 2 }}>
      <Container
        maxWidth={wide ? "lg" : "md"}
        sx={{
          bgcolor: soft ? "#d4b0b555" : "transparent",
          borderRadius: soft ? { xs: 5, md: 7 } : 0,
          p: soft ? { xs: 3.5, md: 6 } : 0,
        }}
      >
        {children}
      </Container>
    </Box>
  );
}

function Title({ children }) {
  return (
    <Typography variant="h3" sx={sectionTitle}>
      {children}
    </Typography>
  );
}

function Text({ children, center = false, light = false, sx={}, ...rest }) {
  return (
    <Typography
      sx={{
        ...textStyle,
        textAlign: center ? "center" : "right",
        color: light ? "#FFF7EE" : "inherit",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Typography>
  );
}

function Callout({ children }) {
  return (
    <Box sx={calloutBox}>
      <Typography sx={{ fontSize: { xs: 21, md: 25 }, fontWeight: 700, lineHeight: 1.65 }}>
        {children}
      </Typography>
    </Box>
  );
}

function InfoCard({ children, icon }) {
  return (
    <Box sx={infoCard}>
      <Typography sx={{ fontSize: 25, mb: 1 }}>{icon}</Typography>
      <Typography sx={{ fontSize: { xs: 18, md: 20 }, lineHeight: 1.65, fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

function ImageFrame({ children }) {
  return <Box sx={imageFrame}>{children}</Box>;
}

const heroOverlay = {
  width: "100%",
  py: { xs: 8, md: 13 },
  px: 2,
  background:
    "linear-gradient(180deg, rgba(36,35,40,0.48) 0%, rgba(36,35,40,0.22) 52%, rgba(36,35,40,0.60) 100%)",
};

const heroTitle = {
  fontSize: { xs: 42, md: 68 },
  lineHeight: 1.08,
  fontWeight: 700,
  textShadow: "0 4px 24px rgba(0,0,0,0.45)",
};

const heroSubtitle = {
  maxWidth: 760,
  fontSize: { xs: 22, md: 30 },
  lineHeight: 1.45,
  textShadow: "0 4px 18px rgba(0,0,0,0.35)",
};

const heroChip = {
  textShadow: "none",
  bgcolor: "rgba(255,255,255,0.72)",
  color: "#242328",
  fontSize: { xs: 15, md: 17 },
  px: 1.2,
  py: 2.3,
  boxShadow: "0 10px 25px rgba(58,42,34,0.14)",
};

const sectionTitle = {
  fontWeight: 700,
  lineHeight: 1.25,
  mb: 1,
};

const textStyle = {
  fontSize: { xs: 19, md: 22 },
  lineHeight: 1.85,
};

const calloutBox = {
  bgcolor: "#d4b0b555",
  borderRadius: 5,
  p: { xs: 2.5, md: 3.2 },
  borderRight: "5px solid #d4b0b5",
};

const imageFrame = {
  bgcolor: "#d4b0b555",
  borderRadius: { xs: 5, md: 7 },
  p: { xs: 3, md: 4 },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const infoCard = {
  height: "100%",
  bgcolor: "#FFF9F1",
  borderRadius: 4,
  p: 3,
  textAlign: "center",
  boxShadow: "0 14px 35px rgba(58,42,34,0.08)",
  border: "1px solid rgba(212,176,181,0.35)",
};

const darkSectionOverlay = {
  width: "100%",
  py: { xs: 7, md: 10 },
  px: 2,
  background:
    "linear-gradient(180deg, rgba(36,35,40,0.72), rgba(36,35,40,0.55))",
};

const darkTitle = {
  fontWeight: 700,
  textShadow: "0 4px 18px rgba(0,0,0,0.35)",
};

const darkBold = {
  color: "#FFF7EE",
  fontSize: { xs: 21, md: 24 },
  fontWeight: 700,
  lineHeight: 1.75,
  mt: 2,
};

const smallNote = {
  mt: 3,
  color: "#FFF7EE",
  fontSize: { xs: 14, md: 15 },
  lineHeight: 1.8,
  opacity: 0.9,
  border: "1px solid rgba(255,255,255,0.28)",
  borderRadius: 4,
  p: 2.5,
};

const bonusItem = {
  fontSize: { xs: 18, md: 20 },
  fontWeight: 700,
  lineHeight: 1.6,
};

const priceBox = {
  bgcolor: "#FFF7EE",
  borderRadius: 5,
  p: { xs: 3, md: 4 },
  my: 2,
  minWidth: { xs: "100%", sm: 360 },
  boxShadow: "0 18px 50px rgba(58,42,34,0.12)",
};

const ctaButton = {
  borderRadius: 999,
  px: 4,
  py: 1.8,
  fontSize: 20,
  boxShadow: "0 16px 36px rgba(138,90,60,0.28)",
  transition: "0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
};

const PriceSection = React.forwardRef((props, ref)=>{
  return (
    <Box sx={priceSectionWrap}>
      <Container maxWidth="md">
        <Box sx={priceShell} ref={ref}>
          <Stack direction="column" gap={4} justifyContent="center" alignItems="center">
            <Stack spacing={2.2} sx={{ height: "100%", justifyContent: "center" }}>

              <Typography sx={priceLeadText}>
                אני רוצה שכמה שיותר אנשים ילמדו לעבוד עם האנרגיה שלהם בעצמם -
                ולא להיות תלויים במטפלים או באנשי מקצוע בכל פעם שהם מרגישים שמשהו תקוע.
              </Typography>

              <Box sx={priceValueBox}>
                <Typography sx={priceValueItem}>
                  שיעור אישי איתי מתחיל ב־444 ₪
                </Typography>
                <Typography sx={priceValueItem}>
                  הגעה לטיהור בית פרטי הוא תהליך שמתחיל בכ־750 ₪
                </Typography>
                <Typography sx={priceValueItem}>
                  הסדנה הזו מרכזת ידע, כלים ותרגולים שאני בדרך כלל חולקת במפגשים אישיים בלבד
                </Typography>
              </Box>

              <Typography sx={priceLeadText}>
                לכבוד ההשקה הראשונה בחרתי לפתוח אותה במחיר נגיש במיוחד:
              </Typography>
            </Stack>
            <Box sx={priceCard}>
              <Typography sx={priceBadge}>מחיר השקה מיוחד</Typography>

              <Typography variant="h4" sx={{fontWeight: 'bold'}}>222 ₪</Typography>
              
              <Typography sx={priceOnly}>בלבד</Typography>

              <Divider sx={priceDivider} />

              <Typography sx={includedTitle}>כולל:</Typography>

              <Stack spacing={1.35} sx={{ mb: 3 }}>
                {[
                  "הסדנה המלאה",
                  "טקס הסרת אנרגיות שליליות ועין הרע בלייב",
                  "הקלטה לצפייה חוזרת",
                  "מדריך החומרים השלם לטיהור וברכה",
                ].map((item) => (
                  <Stack
                    key={item}
                    direction="row"
                    spacing={1.2}
                    alignItems="flex-start"
                    sx={{ textAlign: "right" }}
                  >
                    <Typography sx={checkIcon}>✔️</Typography>
                    <Typography sx={includedText}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Typography sx={limitedText}>
                המחיר הזה תקף להשקה הנוכחית בלבד ומספר המקומות מוגבל
              </Typography>
              <Box marginTop={6}>
                <SCButton onClick={openPayformModal} variant="contained" sx={ctaButton}>
                  אני רוצה להצטרף לסדנה
                </SCButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
});

const priceSectionWrap = {
  py: { xs: 7, md: 10 },
  px: 2,
  bgcolor: "#FFF7EE",
  marginTop: '24px',
};

const priceShell = {
  bgcolor: "#d4b0b555",
  borderRadius: { xs: 5, md: 8 },
  p: { xs: 3, md: 6 },
  boxShadow: "0 24px 70px rgba(58,42,34,0.10)",
  border: "1px solid rgba(212,176,181,0.45)",
};

const priceTitle = {
  fontWeight: 700,
  lineHeight: 1.25,
  mb: 1,
};

const priceLeadText = {
  fontSize: { xs: 19, md: 22 },
  lineHeight: 1.85,
  fontWeight: 700,
};

const priceValueBox = {
  display: "grid",
  gap: 1.4,
  my: 1,
};

const priceValueItem = {
  fontSize: { xs: 18, md: 20 },
  lineHeight: 1.65,
  borderRight: "4px solid #d4b0b5",
  pr: 2,
  py: 0.5,
};

const priceCard = {
  height: "100%",
  bgcolor: "#FFF7EE",
  borderRadius: { xs: 5, md: 7 },
  p: { xs: 3.5, md: 4.5 },
  boxShadow: "0 24px 70px rgba(58,42,34,0.16)",
  border: "1px solid rgba(212,176,181,0.55)",
  textAlign: "center",
};

const priceBadge = {
  display: "inline-flex",
  px: 2.2,
  py: 0.8,
  mb: 1.5,
  borderRadius: 999,
  bgcolor: "#d4b0b555",
  color: "#242328",
  fontSize: 17,
  fontWeight: 700,
};

const priceNumber = {
  fontSize: { xs: 42, md: 54 },
  fontWeight: 800,
  lineHeight: 1,
};

const priceOnly = {
  fontSize: 22,
  fontWeight: 700,
  mt: 0.8,
};

const priceDivider = {
  my: 3,
  borderColor: "rgba(58,42,34,0.16)",
};

const includedTitle = {
  fontSize: 22,
  fontWeight: 700,
  mb: 1.5,
};

const checkIcon = {
  fontSize: 20,
  lineHeight: 1.5,
  minWidth: 28,
};

const includedText = {
  fontSize: { xs: 18, md: 20 },
  lineHeight: 1.55,
  fontWeight: 700,
  textAlign: "right",
};

const limitedText = {
  color: "primary.main",
  fontSize: 17,
  fontWeight: 700,
  lineHeight: 1.6,
};

const priceCtaButton = {
  borderRadius: 999,
  px: 4,
  py: 1.8,
  mt: 2,
  width: "100%",
  fontSize: 20,
  boxShadow: "0 16px 36px rgba(138,90,60,0.28)",
  transition: "0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
};

export default withMainContainer(EnergyCleansingLandingPage, {noHeader: true});
